import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  BarChart3, ArrowLeft, CheckCircle2, AlertCircle, Send, Calendar,
  TrendingUp, Users, Loader2, AlertTriangle, CalendarClock, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtFecha = (iso) => {
  if (!iso) return 'Nunca';
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

function StatCard({ icon: Icon, label, value, hint, accent = 'text-blue-600' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
          <Icon className={`w-4 h-4 ${accent}`} />
        </div>
        <p className={`text-2xl font-bold ${accent}`}>{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

/** Mini-gráfico de barras puro CSS (sin librerías). */
function DailyBarChart({ data }) {
  if (!data?.length) {
    return <p className="text-sm text-muted-foreground text-center py-8">Sin datos en este período</p>;
  }
  const maxTotal = Math.max(1, ...data.map((d) => d.success + d.failed + d.skipped));
  return (
    <div className="flex items-end gap-1 h-40 px-1 overflow-x-auto" data-testid="daily-bar-chart">
      {data.map((d) => {
        const totalH = ((d.success + d.failed + d.skipped) / maxTotal) * 100;
        const total = d.success + d.failed + d.skipped;
        return (
          <div key={d.date} className="flex flex-col items-center flex-1 min-w-[18px]" title={`${d.date}: ${d.success}✓ ${d.failed}✗ ${d.skipped}⊘`}>
            <div className="flex-1 w-full flex items-end" style={{ height: '140px' }}>
              <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${totalH}%`, minHeight: total > 0 ? '4px' : 0 }}>
                {d.success > 0 && <div className="bg-emerald-500" style={{ flex: d.success }} />}
                {d.failed > 0 && <div className="bg-red-500" style={{ flex: d.failed }} />}
                {d.skipped > 0 && <div className="bg-amber-400" style={{ flex: d.skipped }} />}
              </div>
            </div>
            <span className="text-[9px] text-muted-foreground mt-1 -rotate-45 origin-top-left whitespace-nowrap">{d.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ReportesAnalyticsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reportes-programados/analytics/overview?days=${days}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando métricas');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }
  if (!data) return null;

  const t = data.totals;

  return (
    <div className="space-y-4 p-2 md:p-4" data-testid="reportes-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/configuracion/reportes-programados')} data-testid="btn-back-analytics">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Inteligencia de envíos
            </h1>
            <p className="text-sm text-muted-foreground">Métricas y salud de los reportes programados.</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-44" data-testid="select-days">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="365">Último año</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} data-testid="btn-refresh-analytics">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stat-cards">
        <StatCard icon={Send}          label="Envíos"      value={t.total_sends} hint={`${t.manual} manuales · ${t.automatic} auto`} accent="text-blue-600" />
        <StatCard icon={CheckCircle2}  label="Exitosos"    value={t.success}     hint={`${t.success_rate}% éxito`}                 accent="text-emerald-600" />
        <StatCard icon={AlertCircle}   label="Fallidos"    value={t.failed}      hint={t.failed === 0 ? 'Todo bien' : 'Revisar'}    accent="text-red-600" />
        <StatCard icon={AlertTriangle} label="Omitidos"    value={t.skipped}     hint="sin datos para enviar"                       accent="text-amber-600" />
        <StatCard icon={CalendarClock} label="Reportes"    value={data.reportes_total} hint={`${data.reportes_active} activos`}     accent="text-violet-600" />
        <StatCard icon={Users}         label="Destinatarios" value={data.recipients.total_unique} hint={`${data.recipients.sin_actividad_count} sin actividad`} accent="text-slate-700" />
      </div>

      {/* Daily chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" /> Envíos por día
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DailyBarChart data={data.daily} />
          <div className="flex items-center justify-center gap-4 mt-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" /> Éxito</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-sm" /> Fallo</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-400 rounded-sm" /> Omitido (sin datos)</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Top senders */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">🚀 Reportes más activos</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.top_senders.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Sin envíos en este período</p>
            ) : (
              <table className="w-full text-xs" data-testid="top-senders-table">
                <thead className="border-b">
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-1.5 font-medium">Reporte</th>
                    <th className="text-right px-3 py-1.5 font-medium">Envíos</th>
                    <th className="text-right px-3 py-1.5 font-medium">% Éxito</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_senders.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-3 py-1.5 truncate max-w-[200px]">{r.nombre} {!r.enabled && <Badge variant="outline" className="text-[9px] ml-1">deshab.</Badge>}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{r.sends}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${r.success_rate >= 95 ? 'text-emerald-600' : r.success_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{r.success_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Most failures */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">⚠️ Reportes con más fallos</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.most_failures.length === 0 ? (
              <p className="text-xs text-emerald-700 text-center py-6">¡Sin fallos en este período!</p>
            ) : (
              <table className="w-full text-xs" data-testid="failures-table">
                <thead className="border-b">
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-1.5 font-medium">Reporte</th>
                    <th className="text-right px-3 py-1.5 font-medium">Fallos</th>
                    <th className="text-right px-3 py-1.5 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.most_failures.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-3 py-1.5 truncate max-w-[200px]">{r.nombre}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-red-600">{r.failed}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{r.sends}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recipients sin actividad */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4" /> Destinatarios sin actividad reciente
            {data.recipients.sin_actividad_count > 0 && (
              <Badge variant="outline" className="text-[10px]">{data.recipients.sin_actividad_count}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.recipients.sin_actividad.length === 0 ? (
            <p className="text-xs text-emerald-700 text-center py-6">Todos los destinatarios recibieron al menos un reporte en los últimos {days} días.</p>
          ) : (
            <table className="w-full text-xs" data-testid="recipients-table">
              <thead className="border-b">
                <tr className="text-muted-foreground">
                  <th className="text-left px-3 py-1.5 font-medium">Email</th>
                  <th className="text-left px-3 py-1.5 font-medium">Último envío</th>
                  <th className="text-left px-3 py-1.5 font-medium">Reportes</th>
                </tr>
              </thead>
              <tbody>
                {data.recipients.sin_actividad.map((r) => (
                  <tr key={r.email} className="border-b last:border-0" data-testid={`recipient-row-${r.email}`}>
                    <td className="px-3 py-1.5 font-mono">{r.email}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{fmtFecha(r.last_date)}</td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[300px]">{r.reportes.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Reportes deshabilitados */}
      {data.reportes_disabled.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">⏸️ Reportes deshabilitados</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <tbody>
                {data.reportes_disabled.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-3 py-1.5">{r.nombre}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.tipo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
